import { DXG, useDataGrid } from 'components/devexpress/Grid/dx-grid';
import { Field } from 'components/uikit/Field';
import { Input } from 'components/uikit/inputs/Input';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { useMemo, useRef } from 'react';
import formStyles from 'components/form/form.module.scss';
import gridStyles from 'components/devexpress/Grid/grid.module.scss';
import { Button } from 'components/uikit/buttons/Button';
import {
  BooleanParam,
  DateParam,
  StringParam,
  useQueryParams,
} from 'react-router-url-params';
import { QueryFactory } from 'services/api';
import { type IColumnProps, Pager } from 'devextreme-react/cjs/data-grid';
import { Loading } from 'components/uikit/suspense/Loading';
import { jsPDF } from 'jspdf';
import { exportDataGrid } from 'devextreme/pdf_exporter';
import autoTable from 'jspdf-autotable';
import { addHeader, fieldTitleCell } from 'helpers/pdf/pdf-helper';
import { convertAbasTable, getQueryHeadField } from 'helpers/abas-helper';
import { Breadcrumbs } from 'components/uikit/Breadcrumbs';
import { DataGridRef } from 'devextreme-react/data-grid';

type FormParams = {
  deliveryDateFrom: Date;
  deliveryDateTo: Date;
  project: string;
  article: string;
  isOnlyCriticalProcesses: boolean;
};

export const DeliveryObligationPage = () => {
  useDataGrid();
  const [params, setParams] = useQueryParams({
    deliveryDateFrom: DateParam,
    deliveryDateTo: DateParam,
    project: StringParam,
    article: StringParam,
    isOnlyCriticalProcesses: BooleanParam,
    //...pagingSortingQueryParams(2),
  });
  const form = useAdvancedForm<FormParams>(async (data) => {
    setParams(data);
  });

  function getHeadField(field: string) {
    return getQueryHeadField(deliveryObligationQuery, field);
  }

  const deliveryObligationQuery =
    QueryFactory.DeliveryObligationQuery.useGetQuery({
      lieferant: '99999',
      deliveryDateFrom: params.deliveryDateFrom,
      deliveryDateTo: params.deliveryDateTo,
      project: params.project,
      article: params.article,
      isOnlyCriticalProcesses: params.isOnlyCriticalProcesses,
    });
  const data = useMemo(() => {
    return convertAbasTable(deliveryObligationQuery.data?.content.data.table);
  }, [deliveryObligationQuery.data]);

  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  return (
    <div>
      <Breadcrumbs />
      <Loading loading={deliveryObligationQuery.isLoading}>
        <form onSubmit={form.handleSubmitDefault}>
          <div className={formStyles.formRow}>
            <Field title={'Delivery date from'}>
              <Input
                type="date"
                {...form.register('deliveryDateFrom', { valueAsDate: true })}
              />
            </Field>
            <Field title={'Delivery date to'}>
              <Input
                type="date"
                {...form.register('deliveryDateTo', { valueAsDate: true })}
              />
            </Field>
          </div>
          <div className={formStyles.formRow}>
            <Field title={'Project'}>
              <Input {...form.register('project')} />
            </Field>
          </div>
          <div className={formStyles.formRow}>
            <Field title={'Article'}>
              <Input {...form.register('article')} />
            </Field>
          </div>
          <div className={formStyles.formRow}>
            <Field title={'Is only critical processes'}>
              <Input
                type="checkbox"
                {...form.register('isOnlyCriticalProcesses')}
              />
            </Field>
          </div>
          <Button type="submit" title="Execute" />
        </form>
        <div>
          <DXG.DataGrid
            ref={dataGridRef}
            dataSource={data}
            className={gridStyles.editorOutline}
            export={{ enabled: true }}
            rowAlternationEnabled={true}
            onExporting={async (e) => {
              const doc = new jsPDF({
                orientation: 'landscape',
              });

              addHeader(doc, 'Delivery obligation');

              const formValues = form.getValues();
              autoTable(doc, {
                alternateRowStyles: {
                  fillColor: undefined,
                },
                styles: {
                  textColor: 'black',
                  cellPadding: 1,
                },
                tableWidth: 'wrap',
                body: [
                  [
                    fieldTitleCell('Supplier'),
                    getHeadField('lieferant'),
                    '',
                    '',
                  ],
                  [
                    fieldTitleCell('Delivery date from'),
                    formValues.deliveryDateFrom,
                    fieldTitleCell('To'),
                    formValues.deliveryDateTo,
                  ],
                  [fieldTitleCell('Project'), formValues.project],
                  [fieldTitleCell('Article'), formValues.article],
                  [
                    fieldTitleCell('Is only critical processes'),
                    formValues.isOnlyCriticalProcesses,
                  ],
                ],
              });
              const lastY = (doc as any).lastAutoTable.finalY;

              const columnWidths: number[] = [];
              columnsConfig.forEach((value, index) => {
                if (value.disableExport) columnWidths[index] = 0;
              });
              columnWidths[columnsConfig.length] = 0;

              let rowIndex = 0;
              await exportDataGrid({
                columnWidths: columnWidths,
                jsPDFDocument: doc,
                component: e.component,
                topLeft: { x: 0, y: lastY - 5 },
                indent: 0,
                onRowExporting(options) {
                  rowIndex++;
                  if (rowIndex > 1 && rowIndex % 2 === 1) {
                    options.rowCells!.forEach((cell) => {
                      cell.backgroundColor = '#e5e5e5';
                    });
                  }
                },
              });
              doc.save('Delivery_obligation.pdf');
            }}
          >
            {columnsConfig.map((x) => (
              <DXG.Column {...x} showEditorAlways={x.allowEditing} />
            ))}
            <Pager
              visible={true}
              allowedPageSizes={[1, 10, 20]}
              showPageSizeSelector={true}
            />
            <DXG.Editing
              mode="cell"
              allowUpdating={true}
              allowAdding={false}
              allowDeleting={false}
            />
            <DXG.Export enabled={true} formats={exportFormats} />
          </DXG.DataGrid>
        </div>
      </Loading>
    </div>
  );
};
const exportFormats = ['pdf'];
const columnsConfig: (IColumnProps & { disableExport?: boolean })[] = [
  {
    dataField: 'kopf',
    allowEditing: false,
    caption: 'Occupy',
  },
  {
    allowEditing: false,
    caption: '',
    width: 50,
  },
  {
    allowEditing: false,
    caption: 'Verv',
  },
  {
    allowEditing: false,
    caption: 'Hinv',
  },
  {
    dataField: 'tkuli',
    allowEditing: false,
    caption: 'Lieferant',
    disableExport: false,
  },
  {
    allowEditing: false,
    caption: '',
    width: 50,
  },
  {
    dataField: 'pnum', // Positionsnummer ?
    allowEditing: false,
    caption: 'Pos',
    disableExport: false,
  },
  {
    dataField: 'art',
    allowEditing: false,
    caption: 'Article',
    disableExport: false,
  },
  {
    allowEditing: false,
    caption: '',
    width: 50,
  },
  {
    dataField: 'mge',
    allowEditing: false,
    caption: 'Amount',
    disableExport: false,
  },
  {
    dataField: 'limge',
    allowEditing: false,
    caption: 'Delivery quantity', // Open delivery quantity
    disableExport: false,
  },
  {
    dataField: 'remge',
    allowEditing: false,
    caption: 'Delivery quantity', // Open invoice  quantity
    disableExport: false,
  },
  {
    dataField: 'tterm',
    allowEditing: false,
    caption: 'Delivery date',
    disableExport: false,
  },
  {
    dataField: 'twterm',
    allowEditing: false,
    caption: 'Target date',
    disableExport: false,
  },
];
